/**
 * createAgentSession() — universal factory for agent sessions.
 *
 * Handles workspace preparation, skaile.yaml writing, runtime selection,
 * startup, and cleanup registration. Returns a ready-to-use {@link AgentSession}.
 *
 * For multi-session managers (forge-tui) or bots (forge-mattermost) that need
 * custom session subclasses, use LocalRuntime directly:
 *
 *   const runtime = new LocalRuntime();
 *   await runtime.start({ projectDir: dir, agentDir });
 *   const session = new MySession(id, name, runtime);
 *
 * @docLink packages/sdk/concepts#create-agent-session
 */

import { AgentClient } from "@skaile/workspaces/client";
import { WebSocketClientTransport } from "@skaile/workspaces/transport/ws/client";
import { AgentSession } from "./agent-session.js";
import { LocalRuntime } from "./local-runtime.js";
import type { CreateAgentSessionOptions } from "./types.js";
import { prepareWorkspace, writeSkaileYaml } from "./workspace.js";

export async function createAgentSession(
  opts: CreateAgentSessionOptions = {},
): Promise<AgentSession> {
  const { dir, cleanup } = prepareWorkspace(opts);
  writeSkaileYaml(dir, opts);

  const mode = opts.mode ?? { type: "local" };
  let runtime: LocalRuntime | AgentClient;

  if (mode.type === "local") {
    const r = new LocalRuntime();
    await r.start({ projectDir: dir, agentDir: opts.agentDir });
    runtime = r;
  } else {
    const transport = new WebSocketClientTransport({ url: mode.url });
    await transport.connect();
    runtime = new AgentClient(transport);
  }

  const session = new AgentSession(runtime);
  session.state = "idle";

  if (cleanup) {
    session._registerCleanup(cleanup);
  }

  if (opts.initialPrompt?.trim()) {
    session.send(opts.initialPrompt.trim());
  }

  return session;
}
